import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaTimes } from 'react-icons/fa';
import { canLinkHallProfile, profilePathFor } from './PlayerPopupHeader';

const ROLE_LABELS = {
  creator: 'Creator',
  admin: 'Admin',
  moderator: 'Moderator',
  subscriber: 'Subscriber'
};
const ROLE_RANK = { creator: 4, admin: 3, moderator: 2, subscriber: 1 };

/**
 * Right-click / long-press menu on a player avatar inside a Hall.
 *
 * Actions are gated both client-side (cosmetically, to hide illegal
 * buttons) and server-side (authoritatively).
 */
const HallMemberMenu = ({
  isOpen, onClose,
  hallSlug,
  actor, actorMembership,        // current signed-in user + their membership
  target,                        // { userId, username } of the player being acted on
  onMembershipChanged,            // called with the new membership after any action
  canManageStage = false,
  teamAFull = false,
  teamBFull = false,
  onToggleVip,
  onTeleportTeam
}) => {
  const location = useLocation();
  const [targetMembership, setTargetMembership] = useState(null);
  const [targetUser, setTargetUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null); // string: action name while request is in flight

  useEffect(() => {
    if (!isOpen || !hallSlug || !target?.username) return undefined;
    let cancelled = false;
    setLoading(true);
    setTargetMembership(null);
    setTargetUser(null);
    fetch(
      `/api/halls/${encodeURIComponent(hallSlug)}/members/${encodeURIComponent(target.username)}`,
      { credentials: 'include' }
    )
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (cancelled || !data) return;
        setTargetMembership(data.membership || null);
        setTargetUser(data.user || null);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, hallSlug, target?.username]);

  if (!isOpen || !target) return null;

  const actorIsOwner = actor?.status === 'Owner';
  const actorRank = actorIsOwner
    ? Infinity
    : (actorMembership ? (ROLE_RANK[actorMembership.role] || 0) : 0);
  const targetRank = targetMembership ? (ROLE_RANK[targetMembership.role] || 0) : 0;
  const targetIsOwner = targetUser?.status === 'Owner' || target?.status === 'Owner';

  // Self-protection
  if (target.userId && actor && String(target.userId) === String(actor.userId)) {
    return null;
  }

  const canModerate = actorRank >= ROLE_RANK.moderator;
  const canAdmin = actorRank >= ROLE_RANK.admin;
  const canSetRole = (role) => {
    if (actorIsOwner) return true;
    if (actorRank < ROLE_RANK.admin) return false;
    if (actorRank <= targetRank) return false;
    if (targetIsOwner) return false;
    const newRank = ROLE_RANK[role];
    if (actorRank === ROLE_RANK.admin && newRank >= ROLE_RANK.admin) return false;
    return true;
  };
  const canAct = (action) => {
    if (targetIsOwner) return false;
    if (actorRank <= targetRank) return false;
    if (action === 'ban' || action === 'unban' || action === 'kick') return canAdmin;
    return canModerate;
  };

  const call = async (path, body) => {
    setBusy(path);
    try {
      const res = await fetch(
        `/api/halls/${encodeURIComponent(hallSlug)}/members/${encodeURIComponent(target.username)}/${path}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: body ? { 'Content-Type': 'application/json' } : undefined,
          body: body ? JSON.stringify(body) : undefined
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(data?.error || `Action failed (${res.status})`);
        return;
      }
      setTargetMembership(data.membership);
      onMembershipChanged?.(data.membership);
    } finally {
      setBusy(null);
    }
  };

  const setRole = (role) => call('role', { role });
  const moderate = (action, body) => call(action, body);

  return (
    <div
      className="fixed inset-0 z-[150] flex items-center justify-center bg-black bg-opacity-60"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-gray-900 border border-gray-700 rounded-lg shadow-xl w-80 text-white"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
          {canLinkHallProfile(target) ? (
            <Link
              to={profilePathFor(target.username, location.pathname)}
              onClick={onClose}
              className="font-semibold truncate hover:underline"
            >
              {target.username}
            </Link>
          ) : (
            <div className="font-semibold truncate">{target.username || 'Player'}</div>
          )}
          <button onClick={onClose} className="text-gray-400 hover:text-white" aria-label="Close">
            <FaTimes size={14} />
          </button>
        </div>

        <div className="px-4 py-2 text-xs text-gray-400 border-b border-gray-800">
          {loading
            ? 'Loading…'
            : `Role: ${targetMembership ? (ROLE_LABELS[targetMembership.role] || targetMembership.role) : 'Visitor'}`}
          {!loading && targetMembership?.isMuted && (
            <div className="text-amber-400 mt-1">Hall muted</div>
          )}
          {!loading && targetMembership?.timeoutExpiresAt &&
            new Date(targetMembership.timeoutExpiresAt).getTime() > Date.now() && (
            <div className="text-amber-400 mt-1">
              Timed out until {new Date(targetMembership.timeoutExpiresAt).toLocaleString()}
            </div>
          )}
          {!loading && targetMembership?.isBanned && (
            <div className="text-red-400 mt-1">Banned from this hall</div>
          )}
        </div>

        {canManageStage && (
          <div className="py-2 border-b border-gray-800">
            <div className="px-4 py-1 text-[10px] uppercase tracking-wider text-gray-500">Stage</div>
            <button
              type="button"
              disabled={!!busy}
              onClick={() => {
                const next = !targetMembership?.isVip;
                if (canLinkHallProfile(target) && target.username) {
                  call('vip', { isVip: next });
                } else {
                  onToggleVip?.(target, next);
                }
              }}
              className="w-full text-left px-4 py-1.5 text-sm hover:bg-gray-800"
            >
              {targetMembership?.isVip ? 'Remove VIP (stairs)' : 'VIP (stage stairs)'}
            </button>
            <button
              type="button"
              disabled={teamAFull || !!busy}
              onClick={() => onTeleportTeam?.(target, 'A')}
              className="w-full text-left px-4 py-1.5 text-sm hover:bg-gray-800 disabled:opacity-40"
            >
              {teamAFull ? 'Team A seats full' : 'Sit in Team A'}
            </button>
            <button
              type="button"
              disabled={teamBFull || !!busy}
              onClick={() => onTeleportTeam?.(target, 'B')}
              className="w-full text-left px-4 py-1.5 text-sm hover:bg-gray-800 disabled:opacity-40"
            >
              {teamBFull ? 'Team B seats full' : 'Sit in Team B'}
            </button>
          </div>
        )}

        {loading ? (
          <div className="px-4 py-6 text-sm text-gray-400 text-center">
            Loading member…
          </div>
        ) : !canModerate ? (
          <div className="px-4 py-6 text-sm text-gray-400 text-center">
            You don't have moderation permission in this Hall.
          </div>
        ) : (
          <div className="py-2">
            <div className="px-4 py-1 text-[10px] uppercase tracking-wider text-gray-500">Role</div>
            {Object.keys(ROLE_LABELS).map(role => (
              <button
                key={role}
                type="button"
                disabled={!canSetRole(role) || !!busy}
                onClick={() => setRole(role)}
                className="w-full text-left px-4 py-1.5 text-sm hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Set to {ROLE_LABELS[role]}
              </button>
            ))}

            <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider text-gray-500">Moderation</div>
            <button
              disabled={!canAct('mute') || !!busy}
              onClick={() => moderate(targetMembership?.isMuted ? 'unmute' : 'mute')}
              className="w-full text-left px-4 py-1.5 text-sm hover:bg-gray-800 disabled:opacity-40"
            >
              {targetMembership?.isMuted ? 'Unmute' : 'Mute'}
            </button>
            <button
              disabled={!canAct('timeout') || !!busy}
              onClick={() => moderate('timeout', { seconds: 15 * 60 })}
              className="w-full text-left px-4 py-1.5 text-sm hover:bg-gray-800 disabled:opacity-40"
            >
              Timeout 15 minutes
            </button>
            <button
              disabled={!canAct('timeout') || !!busy}
              onClick={() => moderate('timeout', { seconds: 3600 })}
              className="w-full text-left px-4 py-1.5 text-sm hover:bg-gray-800 disabled:opacity-40"
            >
              Timeout 1 hour
            </button>
            <button
              disabled={!canAct('timeout') || !!busy}
              onClick={() => moderate('timeout', { seconds: 24 * 3600 })}
              className="w-full text-left px-4 py-1.5 text-sm hover:bg-gray-800 disabled:opacity-40"
            >
              Timeout 24 hours
            </button>
            {targetMembership?.timeoutExpiresAt &&
              new Date(targetMembership.timeoutExpiresAt).getTime() > Date.now() && (
              <button
                disabled={!canAct('timeout') || !!busy}
                onClick={() => moderate('timeout', { seconds: 0 })}
                className="w-full text-left px-4 py-1.5 text-sm hover:bg-gray-800 disabled:opacity-40 text-yellow-300"
              >
                Clear timeout
              </button>
            )}

            <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider text-gray-500">Hall access</div>
            <button
              disabled={!canAct('kick') || !!busy}
              onClick={() => moderate('kick')}
              className="w-full text-left px-4 py-1.5 text-sm hover:bg-gray-800 disabled:opacity-40 text-orange-300"
            >
              Kick (end session)
            </button>
            <button
              disabled={!canAct('ban') || !!busy}
              onClick={() => moderate(targetMembership?.isBanned ? 'unban' : 'ban')}
              className="w-full text-left px-4 py-1.5 text-sm hover:bg-gray-800 disabled:opacity-40 text-red-400"
            >
              {targetMembership?.isBanned ? 'Unban' : 'Ban from this Hall'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HallMemberMenu;
